import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    mediaItemId: String,
    folderId: String
  }

  connect() {
    console.log("Image viewer trigger controller connected")
  }

  async open(event) {
    event.preventDefault()
    
    try {
      // Fetch all media items in the folder
      const response = await fetch(`/folders/${this.folderIdValue}/media_items.json`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        console.error('Failed to fetch media items:', response.status, response.statusText)
        throw new Error('Failed to fetch media items')
      }
      
      const data = await response.json()
      console.log('Fetched media items:', data)
      
      // Use all media items (both images and videos)
      const mediaItems = data.media_items
      const currentIndex = mediaItems.findIndex(item => item.id.toString() === this.mediaItemIdValue)
      
      if (currentIndex === -1) {
        console.error('Media item not found:', this.mediaItemIdValue)
        throw new Error('Media item not found')
      }
      
      console.log('Opening viewer for item:', mediaItems[currentIndex])
      this.createModal(mediaItems, currentIndex)
    } catch (error) {
      console.error('Error opening image viewer:', error)
      alert('Unable to open image viewer. Please try again.')
    }
  }

  createModal(mediaItems, currentIndex) {
    // Get the current user ID from the body element
    const currentUserId = document.body.dataset.currentUserId
    
    // Create modal structure with its own controller
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 bg-black bg-opacity-90 z-50'
    modal.setAttribute('data-controller', 'image-viewer-modal')
    modal.setAttribute('data-image-viewer-modal-media-items-value', JSON.stringify(mediaItems))
    modal.setAttribute('data-image-viewer-modal-current-index-value', currentIndex)
    
    modal.innerHTML = `
      <div class="relative w-full h-full flex items-center justify-center">
        <!-- Close button -->
        <button 
          class="absolute top-4 left-4 bg-red-500 hover:bg-red-600 text-black font-medium px-2 py-1 sm:px-4 sm:py-2 rounded-lg z-50 flex items-center gap-1 sm:gap-2 transition-colors duration-200 text-sm sm:text-base" 
          data-action="click->image-viewer-modal#close" 
          type="button">
          <span class="hidden sm:inline">Close</span>
          <span class="sm:hidden">X</span>
          <svg class="hidden sm:block w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Add to Favourite Folder button -->
        <div class="absolute top-4 right-4 z-50">
          <div class="relative" data-controller="favourite-folder-dropdown" data-favourite-folder-dropdown-current-media-item-value='${JSON.stringify(mediaItems[currentIndex])}'>
            <button 
              class="bg-blue-600 hover:bg-blue-700 text-white font-medium px-2 py-1 sm:px-4 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors duration-200 text-sm sm:text-base" 
              data-action="click->favourite-folder-dropdown#toggle"
              data-favourite-folder-dropdown-target="button"
              type="button">
              <span class="hidden sm:inline">Add to Favourite Folder</span>
              <span class="sm:hidden">Favourites</span>
              <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <!-- Dropdown menu (will be populated later) -->
            <div 
              class="hidden absolute top-full right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50" 
              data-favourite-folder-dropdown-target="menu">
              <div class="py-1">
                <!-- Favourite folder options will be loaded here -->
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation buttons -->
        <div class="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
          <button class="text-white hover:text-gray-300 focus:outline-none select-none" data-action="click->image-viewer-modal#showPrevious" type="button">
            <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        <div class="absolute right-4 top-1/2 transform -translate-y-1/2 z-10">
          <button class="text-white hover:text-gray-300 focus:outline-none select-none" data-action="click->image-viewer-modal#showNext" type="button">
            <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <!-- Media container -->
        <div class="relative max-w-full max-h-full p-4" data-image-viewer-modal-target="mediaContainer">
          <!-- Image element (for images) -->
          <img class="max-h-[90vh] max-w-full object-contain transition-opacity duration-200" src="" alt="" style="display: none;" />
          <!-- Video element (for videos) -->
          <video controls autoplay loop muted class="max-h-[90vh] max-w-full" style="display: none;">
            <source src="" type="">
            Your browser does not support the video tag.
          </video>
          <div class="absolute bottom-0 left-0 right-0 text-center text-white text-sm py-2">
            <span data-image-viewer-modal-target="currentIndex"></span> / <span data-image-viewer-modal-target="totalCount"></span>
          </div>
        </div>
      </div>
    `

    // Add to document
    document.body.appendChild(modal)
    
    // Set the current user ID attribute AFTER adding to document
    modal.setAttribute('data-current-user-id', currentUserId)
    
    // Let Stimulus automatically detect and connect to the new controller
    // The modal will be processed on the next tick
  }
} 